import { useMemo } from 'react';
import { formatToCurrency, formatToPercentage } from 'utils';

export interface FinanceFooterHook {
  year: string;
  loan: string;
  interest: string;
  monthly: string;
  total: string;
  duration: number;
}

const getTotalLoanCost = (
  amount: number,
  apr: number,
  terms: number,
): string => {
  const monthlyInterest = apr / 12 / 10000;
  const compoundInterest = (1 + monthlyInterest) ** terms;
  const monthlyLoan =
    (amount * (monthlyInterest * compoundInterest)) / (compoundInterest - 1);
  return formatToCurrency(Math.ceil(monthlyLoan * terms));
};

const getMonthlyCost = (amount: number, apr: number, terms: number): string => {
  const monthlyInterest = apr / 12 / 10000;
  const compoundInterest = (1 + monthlyInterest) ** terms;
  const monthlyLoan =
    (amount * (monthlyInterest * compoundInterest)) / (compoundInterest - 1);
  return formatToCurrency(Math.ceil(monthlyLoan));
};

export default function useFinanceFooter(
  amount = 500000,
  apr = 1290,
  terms = 60,
): FinanceFooterHook {
  const year = useMemo(() => String(new Date().getFullYear()), []);

  const loan = useMemo(() => {
    if (amount % 100 === 0)
      return formatToCurrency(amount, undefined, undefined, undefined, '.');
    return formatToCurrency(amount);
  }, [amount]);

  const interest = useMemo(() => formatToPercentage(apr), [apr]);

  const monthly = useMemo(
    () => getMonthlyCost(amount, apr, terms),
    [amount, apr, terms],
  );

  const total = useMemo(
    () => getTotalLoanCost(amount, apr, terms),
    [amount, apr, terms],
  );

  return {
    year,
    loan,
    monthly,
    total,
    interest,
    duration: terms,
  };
}
